import { useCallback, useMemo } from 'react';

import {
  type SchoolInfoCategoryTree,
  type SchoolInfoFormValues,
} from '../types';

import { useSelectGrade } from './useSelectGrade';
import { useSelectSchoolType } from './useSelectSchoolType';
import { useSelectSemester } from './useSelectSemester';

/**
 * 학교 정보 관리 orchestrator hook
 *
 * useSchoolTypeState, useGradeState, useSemesterState를 조합하여
 * 학교급(schoolType), 학년(grade), 학기(semester) 선택을 관리합니다.
 *
 * Dependency Chain:
 * schoolInfoTree → useSchoolTypeState → selectedSchoolTypeNode
 *                                      ↓
 *                                  useGradeState → selectedGradeNode
 *                                                 ↓
 *                                             useSemesterState
 */
export const useSchoolCategorySelector = ({
  formValues,
  schoolInfoTree,
}: {
  formValues: SchoolInfoFormValues;
  schoolInfoTree: SchoolInfoCategoryTree;
}) => {
  // Dependency Chain: Level 1 - SchoolType
  const schoolTypeState = useSelectSchoolType({
    initialSchoolTypeId: formValues.schoolTypeId,
    schoolInfoTree,
  });

  // Dependency Chain: Level 2 - Grade (depends on selectedSchoolTypeNode)
  const gradeState = useSelectGrade({
    initialGradeId: formValues.gradeId,
    parentNode: schoolTypeState.selectedSchoolTypeNode,
  });

  // Dependency Chain: Level 3 - Semester (depends on selectedGradeNode)
  const semesterState = useSelectSemester({
    initialSemesterId: formValues.semesterId,
    parentNode: gradeState.selectedGradeNode,
  });

  // 통합 에러 객체 (기존 API 호환성 유지)
  const errors = useMemo(
    () => ({
      schoolTypeId: schoolTypeState.error,
      gradeId: gradeState.error,
      semesterId: semesterState.error,
    }),
    [schoolTypeState.error, gradeState.error, semesterState.error],
  );

  const clearError = useCallback(
    (field: string) => {
      switch (field) {
        case 'schoolTypeId':
          schoolTypeState.clearError();
          break;
        case 'gradeId':
          gradeState.clearError();
          break;
        case 'semesterId':
          semesterState.clearError();
          break;
      }
    },
    [schoolTypeState, gradeState, semesterState],
  );

  // 통합 유효성 검사
  const validate = useCallback((): boolean => {
    const schoolTypeValid = schoolTypeState.validate();
    const gradeValid = gradeState.validate();
    const semesterValid = semesterState.validate();

    return schoolTypeValid && gradeValid && semesterValid;
  }, [schoolTypeState, gradeState, semesterState]);

  return {
    schoolTypeState,
    gradeState,
    semesterState,

    // 에러
    errors,
    clearError,

    // 유효성 검사
    validate,
  };
};
